// This should be the first import
import { loadPolyfills, polyfillIntlPlural } from '@/polyfills';
import * as React from 'react';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import * as tokens from '@sunwebgroup/design-tokens/sunweb-sun';
import * as udsTokens from '@sunwebgroup/design-tokens/sunweb-sun-uds';
import { GlobalStyles } from '@sunwebgroup/ui-components';
import { ThemeProvider } from 'styled-components';
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary';
import { BreakpointProvider } from '@/scopes/common/components/utils/breakpoints';
import { getLanguageLocale, IntlProvider, translations as translationsToBeRemoved } from '@/intl';
import { Custom500 } from '@/pages/500';
import { useInitLegacyDesignSystemAfterRehydration } from '@/scopes/common/components/hooks/useInitLegacyDesignSystemAfterRehydration';
import { reactPageRegistry } from '@/react-pages';
import { optimizelyClient, useOptimizely } from '@/scopes/common/infrastructure/ab-testing/useOptimizely';
import { PageFieldsProvider } from '@/cms/fields';
import { CommonDependenciesProvider } from '@/scopes/common/infrastructure/dependencies/common';
import { relativePastTimeResolver } from '@/intl/relativePastTimeResolver';
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient';
import featureFlags from '@/scopes/common/infrastructure/feature-flags';
import { ConfigurationProvider } from '@/scopes/common/infrastructure/config/ConfigProvider';
import type { ReactPageProps } from '@/cms/rendering-strategy/types';
import renderingStrategyAbstractFactory from '@/cms/rendering-strategy/renderingStrategyAbstractFactory';
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient';
import type { AppProps } from 'next/app';
import { brandingFont, generalSansFont } from '@/framework/react-styles';
import { createPriceDetailsApiClient } from '@/scopes/common/infrastructure/price-details/api/priceDetailsApiClient';
import type { SlugStaticProps } from '@/cms/types';
import customIntlOnError from '@/intl/customIntlOnError';

loadPolyfills();

function ReactPage({ children, locale, cmsFields, translations, config }: ReactPageProps) {
  let messages;
  let localeForPolyfills;
  if (!featureFlagClient.isActive('OPTIMIZE_TRANSLATION_IMPORT')) {
    messages = translationsToBeRemoved[locale];
    localeForPolyfills = getLanguageLocale(locale);
  } else {
    messages = translations;
    localeForPolyfills = locale;
  }
  const { userId } = useOptimizely();

  polyfillIntlPlural(localeForPolyfills);

  return (
    <div className={`${brandingFont.variable} ${generalSansFont.variable}`}>
      <CommonDependenciesProvider
        dependencies={{
          env: {
            MEDIA_SERVER_BASE_URL: process.env.NEXT_PUBLIC_MEDIA_SERVER_BASE_URL,
            GOOGLE_ANALYTICS_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
            COOKIE_BOT_ID: process.env.NEXT_PUBLIC_COOKIE_BOT_ID,
            NEWRELIC_SCRIPT_NAME: process.env.NEXT_PUBLIC_NEWRELIC_SCRIPT_NAME,
          },
          relativePastTimeResolver,
          analyticsClient: ga4analyticsClient,
          featureFlagsStore: featureFlags.store,
          priceDetailsApiClient: createPriceDetailsApiClient({ contextItemId: cmsFields.contextItemId }), // For now we add this api client here, as this is a common place for both search page and acco pages
        }}
      >
        <ConfigurationProvider value={config ?? null}>
          <PageFieldsProvider value={{ ...cmsFields }}>
            <OptimizelyProvider optimizely={optimizelyClient} user={{ id: userId }}>
              <IntlProvider locale={locale} messages={messages} onError={customIntlOnError}>
                <ThemeProvider theme={{ tokens, udsTokens, themeName: 'sunweb-sun' }}>
                  <BreakpointProvider>
                    <NewRelicErrorBoundary scope="react-page" fallback={<Custom500 brand="sunweb" />}>
                      <GlobalStyles />
                      {children}
                    </NewRelicErrorBoundary>
                  </BreakpointProvider>
                </ThemeProvider>
              </IntlProvider>
            </OptimizelyProvider>
          </PageFieldsProvider>
        </ConfigurationProvider>
      </CommonDependenciesProvider>
    </div>
  );
}

export default function App(appPageProps: AppProps<SlugStaticProps>) {
  const { Component, pageProps, router } = appPageProps;
  useInitLegacyDesignSystemAfterRehydration();

  const renderingStrategy = renderingStrategyAbstractFactory.createRenderingStrategy(pageProps, reactPageRegistry);
  return renderingStrategy.renderApp({
    Component,
    router,
    ReactPage,
    pageProps,
  });
}
